import {
  createEffect,
  mergeProps,
  onCleanup,
  ParentComponent,
  Show,
} from 'solid-js';
import { createStore } from 'solid-js/store';
import type { ModusTooltipPosition } from '@trimble/types';
import { useHover } from '@trimble/solidjs-utils';

const DEFAULT_SHOW_DELAY_MS = 200;

export interface HoverTooltipProps {
  content: string;
  position?: ModusTooltipPosition;
  disabled?: boolean;
  class?: string;
  showDelay?: number;
}

interface RevealState {
  revealed: boolean;
}

const createRevealStore = (): {
  state: RevealState;
  reveal(): void;
  reset(): void;
} => {
  const [state, setState] = createStore<RevealState>({ revealed: false });
  return {
    state,
    reveal: () => setState('revealed', true),
    reset: () => setState('revealed', false),
  };
};

// Each mounted modus-wc-tooltip creates its own Popper instance that recomputes
// on every scroll/resize, so long lists must not keep one per row. The tooltip is
// only mounted while hovered, as a pointer-transparent overlay over the trigger
// (rather than wrapping it) so the trigger's DOM is never re-parented.
// The delay is applied here rather than via Modus' own show-delay, which is
// bypassed for force-open tooltips.
export const HoverTooltip: ParentComponent<HoverTooltipProps> = (
  initialProps
) => {
  const props = mergeProps({ showDelay: DEFAULT_SHOW_DELAY_MS }, initialProps);
  const { isHovered, ref } = useHover();
  const tooltip = createRevealStore();

  createEffect(() => {
    if (!isHovered()) {
      tooltip.reset();
      return;
    }
    if (props.showDelay <= 0) {
      tooltip.reveal();
      return;
    }
    const timer = setTimeout(tooltip.reveal, props.showDelay);
    onCleanup(() => clearTimeout(timer));
  });

  return (
    <div
      ref={ref}
      data-testid="hover-tooltip"
      class={`relative ${props.class ?? ''}`}
    >
      {props.children}
      <Show when={isHovered() && tooltip.state.revealed && !props.disabled}>
        <modus-wc-tooltip
          class="absolute inset-0 pointer-events-none"
          content={props.content}
          position={props.position}
          force-open={true}
        >
          <div class="absolute inset-0" />
        </modus-wc-tooltip>
      </Show>
    </div>
  );
};
